/**
 * 微信 JS-SDK 签名与配置
 * 调用 /api/wechat/jssdk-signature 获取签名，并配置 wx.config
 * 首页、文章详情页分享配置
 */
import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import IndexPage from 'flarum/forum/components/IndexPage';
import DiscussionPage from 'flarum/forum/components/DiscussionPage';

const WECHAT_JS_SDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';

/** 待应用的分享配置（在 wx.config 成功后应用） */
let pendingShareConfig = null;

/** 上次 wx.config 使用的 URL（SPA 路由变化时需重新签名） */
let lastConfigUrl = null;

/**
 * 动态加载微信 JS-SDK 脚本
 * @returns {Promise<void>}
 */
function loadWechatJSSdk() {
  return new Promise((resolve, reject) => {
    if (typeof wx !== 'undefined') {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = WECHAT_JS_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load WeChat JS SDK'));
    document.head.appendChild(script);
  });
}

/**
 * 获取当前页面 URL（不含 hash，微信签名要求）
 * @returns {string}
 */
function getCurrentPageUrl() {
  return window.location.href.split('#')[0];
}

/**
 * 调用 jssdk-signature 接口获取签名配置
 * @param {string} url - 需要签名的页面 URL
 * @returns {Promise<Object>} 返回 { appId, timestamp, nonceStr, signature, jsApiList }
 */
function fetchJssdkSignature(url) {
  const apiUrl = app.forum.attribute('apiUrl');
  const fullUrl = `${apiUrl}/wechat/jssdk-signature?url=${encodeURIComponent(url)}`;
  console.log('[tf/wechat] fetchJssdkSignature, url:', url, 'fullUrl:', fullUrl);

  return app.request({
    method: 'GET',
    url: fullUrl,
  }).then((response) => {
    const data = response?.data;
    const attrs = data?.attributes;
    if (!attrs || !attrs.appId || !attrs.signature) {
      console.error('[tf/wechat] fetchJssdkSignature: invalid response', { data, attrs });
      throw new Error('Invalid jssdk-signature response');
    }
    console.log('[tf/wechat] fetchJssdkSignature: success, appId:', attrs.appId);
    return {
      appId: attrs.appId,
      timestamp: attrs.timestamp,
      nonceStr: attrs.nonceStr,
      signature: attrs.signature,
      jsApiList: (attrs.jsApiList && attrs.jsApiList.length) ? attrs.jsApiList : ['updateAppMessageShareData', 'updateTimelineShareData'],
      debug: attrs.debug || false,
    };
  });
}

/**
 * 执行 wx.config（用指定 URL 获取签名并配置）
 */
function runWxConfig(url) {
  if (!url || typeof wx === 'undefined') return;
  return fetchJssdkSignature(url).then((config) => {
    lastConfigUrl = url;
    wx.config({
      debug: config.debug,
      appId: config.appId,
      timestamp: config.timestamp,
      nonceStr: config.nonceStr,
      signature: config.signature,
      jsApiList: config.jsApiList,
    });
    wx.ready(() => {
      if (pendingShareConfig) {
        wx.updateAppMessageShareData(pendingShareConfig);
        wx.updateTimelineShareData(pendingShareConfig);
      }
    });
    wx.error((err) => {
      console.error('[tf/wechat] wx.error (config failed):', err);
    });
  });
}

/**
 * 初始化微信 JS-SDK（首次加载）
 */
function initWechatJssdk() {
  const url = getCurrentPageUrl();
  if (!url) return;

  loadWechatJSSdk()
    .then(() => runWxConfig(url))
    .catch((err) => {
      console.warn('[tf/wechat] JS-SDK init failed:', err);
    });
}

/**
 * SPA 路由变化后，URL 与上次 wx.config 不一致时需重新签名
 */
function refreshWechatConfigIfNeeded() {
  const url = getCurrentPageUrl();
  if (!url || typeof wx === 'undefined') return;
  if (url === lastConfigUrl) return;
  runWxConfig(url).catch((err) => {
    console.warn('[tf/wechat] refreshWechatConfig failed:', err);
  });
}

/**
 * 设置当前页面的微信分享配置（供各页面调用）
 * @param {Object} config - { title, desc, link, imgUrl }
 */
function setWechatShareConfig(config) {
  if (!config) return;
  const { title, desc, link, imgUrl } = config;
  pendingShareConfig = {
    title: title || document.title,
    desc: desc || '',
    link: link || getCurrentPageUrl(),
    imgUrl: imgUrl || '',
  };
  if (typeof wx === 'undefined') return;
  // SPA 客户端导航后 URL 变化，需重新 wx.config
  refreshWechatConfigIfNeeded();
  wx.ready(() => {
    if (!pendingShareConfig) return;
    wx.updateAppMessageShareData(pendingShareConfig);
    wx.updateTimelineShareData(pendingShareConfig);
  });
}

/**
 * 从 discussion 获取分享用描述（synopsis 或首帖摘要）
 */
function getDiscussionShareDesc(discussion) {
  let text = '';
  try {
    if (discussion.attribute && discussion.attribute('synopsis')) {
      text = discussion.attribute('synopsis');
    } else if (discussion.data?.attributes?.synopsis) {
      text = discussion.data.attributes.synopsis;
    }
  } catch (e) {}
  if (text && typeof text === 'string') {
    return text.replace(/<[^>]*>/g, '').trim().substring(0, 150);
  }
  try {
    const firstPost = discussion.firstPost ? discussion.firstPost() : null;
    if (firstPost) {
      const html = firstPost.contentHtml ? firstPost.contentHtml() : (firstPost.data?.attributes?.contentHtml || '');
      if (html) {
        const div = document.createElement('div');
        div.innerHTML = html;
        text = (div.textContent || div.innerText || '').replace(/\s+/g, ' ').trim();
        return text.substring(0, 150) + (text.length > 150 ? '...' : '');
      }
    }
  } catch (e) {}
  return '';
}

/**
 * 从 discussion 获取分享用图片 URL
 */
function getDiscussionShareImgUrl(discussion) {
  const baseUrl = app.forum.attribute('baseUrl') || '';
  const attrs = discussion.data?.attributes || {};
  const courseInfo = attrs.groupCourse || (discussion.attribute ? discussion.attribute('groupCourse') : null);
  const coverImage = courseInfo?.coverImage || attrs.customThumbnail || attrs.thumbnail || null;
  if (!coverImage) return '';
  if (typeof coverImage === 'string' && (coverImage.startsWith('http') || coverImage.startsWith('//'))) {
    return coverImage;
  }
  const path = typeof coverImage === 'string' ? coverImage.replace(/^\//, '') : '';
  return path ? baseUrl + (baseUrl.endsWith('/') ? '' : '/') + path : '';
}

app.initializers.add('tf-wechat', () => {
  // 页面加载完成后初始化微信 JS-SDK
  initWechatJssdk();
  console.log('[tf/wechat] initializer loaded, initWechatJssdk started');
  // 暴露给其他插件调用（如 tf/groups 课程群组页）
  app.setWechatShareConfig = setWechatShareConfig;

  // 首页微信分享
  extend(IndexPage.prototype, 'oncreate', function () {
    console.log('[tf/wechat] IndexPage oncreate: setting share config');
    const title = 'Community';
    const desc =  'Welcome to our interactive community section, a platform where expatriates can freely engage and enjoy their lives in Shanghai.';
    const shareConfig = { title, desc, link: getCurrentPageUrl(), imgUrl: 'https://community.citynewsservice.cn/assets/social_groups/1770881788_o6oYoC7VJH.jpg' };
    console.log('[tf/wechat] IndexPage shareConfig:', shareConfig);
    setWechatShareConfig(shareConfig);
  });

  // 文章详情页微信分享
  extend(DiscussionPage.prototype, 'show', function (ret, discussion) {
    console.log('[tf/wechat] DiscussionPage show: setting share config, discussionId:', discussion ? (typeof discussion.id === 'function' ? discussion.id() : discussion.id) : null);
    if (!discussion) {
      console.warn('[tf/wechat] DiscussionPage show: discussion is null, skip');
      return;
    }
    const title = discussion.title ? discussion.title() : (discussion.data?.attributes?.title || '');
    const desc = getDiscussionShareDesc(discussion);
    const imgUrl = getDiscussionShareImgUrl(discussion);
    const shareConfig = { title, desc, link: getCurrentPageUrl(), imgUrl };
    console.log('[tf/wechat] DiscussionPage shareConfig:', shareConfig);
    setWechatShareConfig(shareConfig);
  });
});
